/**
 * DiagramSelectionModal - "Create Diagram" modal
 *
 * Allows users to generate additional diagram types on demand.
 * Excludes explorer-backed types (callGraph, importGraph, e2eFlow)
 * which are auto-generated during analysis.
 *
 * Shows methodology, confidence level, and AI requirement for each type.
 */

import { useState, useEffect } from 'react';
import { diagramDescriptions } from '@veodiagram/shared';
import { useAIStatusStore } from '../../stores/aiStatusStore';

interface DiagramTypeInfo {
  type: string;
  name: string;
  methodology: string;
  confidence: string;
  confidenceLevel: 'high' | 'moderate';
  requiresAI: boolean;
  category: 'architecture' | 'ai-powered';
}

const DIAGRAM_TYPES: DiagramTypeInfo[] = [
  // AI-Powered
  { type: 'systemContext', name: 'System Context', methodology: 'AI analyzes codebase for external integrations and user types', confidence: '~85%', confidenceLevel: 'moderate', requiresAI: true, category: 'ai-powered' },
];

interface CategoryGroup {
  label: string;
  key: string;
  diagrams: DiagramTypeInfo[];
}

const CATEGORIES: CategoryGroup[] = [
  { label: 'AI-Powered (Requires API Key)', key: 'ai-powered', diagrams: DIAGRAM_TYPES.filter(d => d.category === 'ai-powered') },
];

interface DiagramSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onStartAnalysis: (diagramTypes: string[]) => void;
  isAnalyzing: boolean;
}

export function DiagramSelectionModal({
  isOpen,
  onClose,
  onStartAnalysis,
  isAnalyzing,
}: DiagramSelectionModalProps) {
  const { status: aiStatus, fetchStatus } = useAIStatusStore();
  const [selectedTypes, setSelectedTypes] = useState<Set<string>>(new Set());

  // Fetch AI status when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchStatus();
      // Pre-select all non-AI types; include AI types only if AI is available
      const defaults = DIAGRAM_TYPES
        .filter(d => !d.requiresAI || aiStatus?.available)
        .map(d => d.type);
      setSelectedTypes(new Set(defaults));
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const aiAvailable = aiStatus?.available ?? false;

  const toggleDiagram = (type: string) => {
    const next = new Set(selectedTypes);
    if (next.has(type)) {
      next.delete(type);
    } else {
      next.add(type);
    }
    setSelectedTypes(next);
  };

  const toggleCategory = (category: CategoryGroup) => {
    const available = category.diagrams.filter(d => !d.requiresAI || aiAvailable);
    const allSelected = available.every(d => selectedTypes.has(d.type));
    const next = new Set(selectedTypes);
    if (allSelected) {
      available.forEach(d => next.delete(d.type));
    } else {
      available.forEach(d => next.add(d.type));
    }
    setSelectedTypes(next);
  };

  const selectAll = () => {
    const all = DIAGRAM_TYPES.filter(d => !d.requiresAI || aiAvailable).map(d => d.type);
    setSelectedTypes(new Set(all));
  };

  const deselectAll = () => setSelectedTypes(new Set());

  const handleStart = () => {
    if (selectedTypes.size > 0) {
      onStartAnalysis(Array.from(selectedTypes));
    }
  };

  const totalAvailable = DIAGRAM_TYPES.filter(d => !d.requiresAI || aiAvailable).length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-background border border-border rounded-lg shadow-xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-border">
          <h2 className="text-xl font-semibold">Create Additional Diagrams</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Generate on-demand diagrams to analyze specific aspects of your codebase.
          </p>
        </div>

        {/* Selection controls */}
        <div className="px-6 py-3 border-b border-border flex items-center gap-4">
          <button onClick={selectAll} className="text-sm text-primary hover:underline">
            Select All
          </button>
          <button onClick={deselectAll} className="text-sm text-primary hover:underline">
            Deselect All
          </button>
          <span className="text-sm text-muted-foreground ml-auto">
            {selectedTypes.size} of {totalAvailable} selected
          </span>
        </div>

        {/* Diagram list */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          {CATEGORIES.map((category) => {
            const available = category.diagrams.filter(d => !d.requiresAI || aiAvailable);
            const selectedInCategory = available.filter(d => selectedTypes.has(d.type)).length;
            const allSelected = selectedInCategory === available.length && available.length > 0;

            return (
              <div key={category.key}>
                {/* Category header */}
                <div className="flex items-center gap-2 mb-3">
                  <button
                    onClick={() => toggleCategory(category)}
                    className="flex items-center gap-2 text-sm font-medium hover:text-primary transition-colors"
                  >
                    <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                      allSelected
                        ? 'bg-primary border-primary'
                        : selectedInCategory > 0
                          ? 'bg-primary/30 border-primary'
                          : 'border-border'
                    }`}>
                      {allSelected && (
                        <svg className="w-3 h-3 text-primary-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                      {!allSelected && selectedInCategory > 0 && (
                        <div className="w-2 h-2 bg-primary rounded-sm" />
                      )}
                    </div>
                    {category.label}
                  </button>
                </div>

                {/* Diagram cards */}
                <div className="space-y-2 ml-6">
                  {category.diagrams.map((diagram) => {
                    const isDisabled = diagram.requiresAI && !aiAvailable;
                    const isSelected = selectedTypes.has(diagram.type);
                    // Use methodology from diagramDescriptions if available, fall back to inline
                    const methodology = diagramDescriptions[diagram.type]?.methodology || diagram.methodology;

                    return (
                      <label
                        key={diagram.type}
                        className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                          isDisabled
                            ? 'opacity-50 cursor-not-allowed border-border bg-muted/30'
                            : isSelected
                              ? 'border-primary bg-primary/5'
                              : 'border-border hover:border-primary/50 hover:bg-muted/50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={isDisabled}
                          onChange={() => !isDisabled && toggleDiagram(diagram.type)}
                          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary disabled:opacity-50"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium">{diagram.name}</span>
                            {/* Confidence badge */}
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                              diagram.confidenceLevel === 'high'
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                                : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300'
                            }`}>
                              {diagram.confidenceLevel === 'high' ? 'High' : 'Moderate'} {diagram.confidence}
                            </span>
                            {/* AI badge */}
                            {diagram.requiresAI && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                                Requires AI
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">{methodology}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* AI not available warning */}
          {!aiAvailable && (
            <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div>
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                    AI-powered diagrams unavailable
                  </p>
                  <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                    Configure your API key in Settings to enable AI-powered diagram generation.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isAnalyzing}
            className="px-4 py-2 text-sm font-medium border border-border rounded-md hover:bg-muted transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleStart}
            disabled={selectedTypes.size === 0 || isAnalyzing}
            className="px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {isAnalyzing ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Creating...
              </>
            ) : (
              <>
                Create Diagrams
                {selectedTypes.size > 0 && (
                  <span className="text-xs opacity-75">({selectedTypes.size})</span>
                )}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
